import '@xyflow/react/dist/style.css';

import { Node as ReactFlowNode } from '@xyflow/react';

import { ThemedReactFlow } from './components/ThemedReactFlow';
import { useClassName } from './hooks/internal/useClassName';
import { useDefaultProps } from './hooks/internal/useDefaultProps';
import { useInternalOnNodeDrag } from './hooks/internal/useInternalOnNodeDrag';
import { useInternalOnNodeDragStop } from './hooks/internal/useInternalOnNodeDragStop';
import { useInternalOnNodesChange } from './hooks/internal/useInternalOnNodesChange';
import { useInternalOnSelectionEnd } from './hooks/internal/useInternalOnSelectionEnd';
import { usePreventScrolling } from './hooks/internal/usePreventScrolling';
import { useResetIsDirty } from './hooks/internal/useResetIsDirty';
import { useSlots } from './hooks/internal/useSlots';
import { useSectionNodeResizeListeners } from './nodes/SectionLabelNode/hooks';
import { FlowChartProps, Node } from './types';

/**
 * @description Wrapper around ReactFlow providing basic functionality, styling, types, etc.
 */
export function FlowChart<T extends ReactFlowNode = Node>(
  componentProps: FlowChartProps<T>
) {
  const { internalProps, externalProps } = useDefaultProps<T>(componentProps);

  const {
    children,
    onNodesChange: _oNC,
    onNodeDrag: _oND,
    onNodeDragStop: _oNDS,
    onSelectionEnd: _oSE,
    className: _className,
    preventScrolling: _preventScrolling,
    ...reactFlowProps
  } = internalProps;

  const { loading, getState, slots: _slots, ...themeProps } = externalProps;

  const className = useClassName({ ...internalProps, ...externalProps });
  const slots = useSlots(externalProps);
  const preventScrolling = usePreventScrolling<T>(internalProps);
  const onNodesChange = useInternalOnNodesChange<T>(internalProps);
  const onNodeDrag = useInternalOnNodeDrag<T>(internalProps);
  const onNodeDragStop = useInternalOnNodeDragStop<T>(internalProps);
  const onSelectionEnd = useInternalOnSelectionEnd<T>(internalProps);

  useResetIsDirty();
  useSectionNodeResizeListeners(getState);

  return (
    <ThemedReactFlow<T>
      onNodesChange={onNodesChange}
      onNodeDrag={onNodeDrag}
      onNodeDragStop={onNodeDragStop}
      onSelectionEnd={onSelectionEnd}
      className={className}
      preventScrolling={preventScrolling}
      {...themeProps}
      {...reactFlowProps}
    >
      <>
        {<slots.MarkerDefinitions />}
        {loading && <slots.Loader />}
        {children}
      </>
    </ThemedReactFlow>
  );
}
